/**
 * Getting Started guide (T10) — merchant-facing, step-by-step, each step
 * ending in a verifiable checkpoint ("you should now see…") so a merchant can
 * tell whether it worked. Public route, linked from the marketing footer and
 * from the app. English-only, all strings via the T9 catalog.
 */
import { CheckCircle2 } from 'lucide-react';
import { t, type MessageKey } from '../i18n';
import { ComingSoonBadge } from '@/components/ui/ComingSoonBadge';
import { isFeatureLive, type FeatureKey } from '@/config/featureAvailability';
import { Navbar } from './Landing/components/Navbar';
import { Footer } from './Landing/components/Footer';

interface GuideStep {
  title: MessageKey;
  body: MessageKey;
  check: MessageKey;
  /** A step a new customer cannot complete yet reads Coming soon (config/featureAvailability.ts). */
  feature?: FeatureKey;
}

const STEPS: GuideStep[] = [
  { title: 'guide.step1.title', body: 'guide.step1.body', check: 'guide.step1.check' },
  { title: 'guide.step2.title', body: 'guide.step2.body', check: 'guide.step2.check' },
  // Messaging channels: live since Meta App Review (2026-10-10); the label
  // follows the registry.
  { title: 'guide.step3.title', body: 'guide.step3.body', check: 'guide.step3.check', feature: 'messenger.dmAutomation' },
  { title: 'guide.step4.title', body: 'guide.step4.body', check: 'guide.step4.check' },
  { title: 'guide.step5.title', body: 'guide.step5.body', check: 'guide.step5.check' },
];

export default function GettingStarted() {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-600">
      <Navbar />

      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{t('guide.title')}</h1>
        <p className="text-gray-500 text-lg mb-12">{t('guide.intro')}</p>

        <div className="space-y-10">
          {STEPS.map((step) => (
            <section key={step.title}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                {t(step.title)}
                {step.feature !== undefined && !isFeatureLive(step.feature) && <ComingSoonBadge />}
              </h2>
              <p className="leading-relaxed mb-4">{t(step.body)}</p>
              <div className="flex items-start gap-2.5 p-3.5 bg-green-50 border border-green-100 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-800 leading-relaxed">
                  <span className="font-semibold">{t('guide.checkpoint')}: </span>
                  {t(step.check)}
                </p>
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
